<script setup lang="ts">
/**
 * Кнопки в шапке GitLab-блока: поднять или погасить стенд, посмотреть флаги,
 * собрать релиз. Из макета 13:461.
 *
 * Кнопки не показывают состояние, а меняют его, поэтому у них своя проверка
 * прав: без `tracker:devops` их не видно совсем. Прятать, а не блокировать —
 * потому что заперта кнопка выглядит как поломка, а не как «вам нельзя».
 *
 * После запуска блок перечитывается: стенд появится строкой, а трекер узнает
 * о нём из журнала действий, а не из ответа кнопки.
 */
import { AppButton } from '@vinprop/design-system'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/features/auth/store'

import { dropStand, raiseStand } from '../api'
import FlagsDialog from './FlagsDialog.vue'
import ReleaseDialog from './ReleaseDialog.vue'
import StandDialog from './StandDialog.vue'

import type { DevBranch, DevStand } from '@/features/issue/devinfo'

const props = defineProps<{
  issueId: string
  issueKey: string
  epicId?: string
  branches: DevBranch[]
  stands: DevStand[]
}>()

const emit = defineEmits<{ done: []; failed: [error: unknown] }>()

const { t } = useI18n()
const auth = useAuthStore()

const dialog = ref<'stand' | 'flags' | 'release' | null>(null)
const busy = ref(false)

/** Права на действия: смотреть блок можно всем, запускать — не всем. */
const allowed = computed(() => auth.can('tracker:devops'))

const live = computed(() => props.stands.filter((stand) => !stand.isRemoved))

const runStand = async (projectPath: string, branch: string) => {
  busy.value = true

  try {
    if (live.value.length) await dropStand(projectPath, branch)
    else await raiseStand(props.issueId, projectPath, branch)

    dialog.value = null
    emit('done')
  } catch (failure) {
    emit('failed', failure)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div v-if="allowed" class="devact">
    <AppButton size="small" variant="secondary" @click="dialog = 'stand'">
      {{ live.length ? t('devops.stand.drop') : t('devops.stand.raise') }}
    </AppButton>
    <AppButton size="small" variant="secondary" @click="dialog = 'flags'">
      {{ t('devops.flags.button') }}
    </AppButton>
    <AppButton size="small" variant="secondary" @click="dialog = 'release'">
      {{ t('devops.release.button') }}
    </AppButton>

    <StandDialog
      v-if="dialog === 'stand'"
      :branches="branches"
      :stands="stands"
      :drop="Boolean(live.length)"
      :busy="busy"
      @submit="runStand"
      @close="dialog = null"
    />

    <FlagsDialog
      v-if="dialog === 'flags'"
      @failed="emit('failed', $event)"
      @close="dialog = null"
    />

    <ReleaseDialog
      v-if="dialog === 'release'"
      :issue-id="issueId"
      :issue-key="issueKey"
      :epic-id="epicId"
      @done="((dialog = null), emit('done'))"
      @failed="emit('failed', $event)"
      @close="dialog = null"
    />
  </div>
</template>

<style scoped>
/* Кнопки макета: высота 32, радиус 8, тихая рамка — они рядом с заголовком
   блока и не должны спорить с ним за внимание. */
.devact {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
